import { Form, Grid, TextArea } from 'semantic-ui-react'
import { Field, Form as FinalForm, FormSpy } from 'react-final-form'
import { SelectionOptions } from '../SelectionOptions/SelectionOptions'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import LocaleSpecificSelect from './LocaleSpecificSelect/LocaleSpecificSelect'
import {
  useLocaleSpecificContext,
  useLocaleSpecificUpdateContext,
} from '../Contexts/LocaleSpecificContext'
import { FileDrop, ImageList, uploadFileToS3 } from '@iheartjane/react-image-list'
import { getLocalizations, saveLocalizations, setLocalizations } from '../../redux/actions/products'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from '../../redux/store'
import { API_S3_UPLOAD, LINEAGE_OPTIONS } from '../../consts'
import {
  ArrayFieldLabelStyle,
  StyledProductImage,
  StyledProductImagesHolder,
} from '../Products/styles'
import { Button, Icon, Message } from 'semantic-ui-react'
import { ResponsiveImage } from '@iheartjane/react-responsive-image'
import { ApiLocalizationWithStatus, UILocalizations } from '../../redux/types/products'
import { ApiLocalization } from '../../types'
import { useImageList } from '@iheartjane/react-image-list'
import axios from 'axios'
import { toast } from 'react-toastify'
import { TrackJS } from 'trackjs'

interface Props {
  productId?: string
  mode: string
}

const TextNumberInput = (inputProps: any) => {
  return (
    <Form.Field inline>
      <label className="product-form-label">{inputProps.label}</label>
      <input placeholder={inputProps.placeholder} {...inputProps.props.input} />
    </Form.Field>
  )
}

const ImagesComponent = ({ productId, currentLocalization, country, subdivision }: any) => {
  const dispatch = useDispatch()
  const localizations: UILocalizations = useSelector(
    (state: AppState) => state.products.localizations
  )
  const productUuid = useSelector((state: AppState) => state.products.productUuid)
  const {
    images,
    addImage: imageListAddImage,
    removeImage: imageListRemoveImage,
    resetImages,
  } = useImageList()

  useEffect(() => {
    resetImages(currentLocalization.images)
  }, [JSON.stringify(currentLocalization.images)]) // eslint-disable-line

  useEffect(() => {
    if (productId && currentLocalization && localizations[productId]) {
      dispatch(
        setLocalizations({
          productUuid,
          productId,
          localizations: [
            ...localizations[productId].filter(
              (l: ApiLocalization) => l.country !== country || l.subdivision !== subdivision
            ),
            {
              ...currentLocalization,
              images: images.length === 0 ? null : images,
            },
          ],
        })
      )
    }
  }, [JSON.stringify(images)]) // eslint-disable-line

  const addImage = (url: string) => {
    imageListAddImage(url)
  }

  const removeImage = (url: string) => {
    imageListRemoveImage(url)
  }

  const filePicker = useRef<HTMLInputElement | null>(null)

  const addFiles = (files: FileList) => {
    ;[...files].forEach(async (file) => {
      const url = await uploadFileToS3(API_S3_UPLOAD, file, {
        credentials: 'include',
      })
      addImage(url)
    })
  }

  const addImageClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    filePicker.current?.click()
  }

  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files)
    }
  }

  return (
    <FileDrop onFilesAdded={addFiles}>
      <ArrayFieldLabelStyle>Photos</ArrayFieldLabelStyle>
      <input
        type="file"
        value={''}
        multiple
        style={{ display: 'none' }}
        onChange={onFilesPicked}
        ref={filePicker}
      />
      <button
        type="button"
        style={{ marginLeft: 10, display: 'inline-block' }}
        onClick={addImageClick}
      >
        <Icon name="add" size="small" />
        Add
      </button>

      <ImageList.Images axis="y" useDragHandle>
        <StyledProductImagesHolder>
          {images?.map((image: string, index: any) => (
            <ImageList.Image key={image} index={index}>
              <div style={{ position: 'relative' }}>
                <ImageList.ImageHandle>
                  {image && (
                    <ResponsiveImage
                      className="ui small bordered image"
                      style={{ borderRadius: '5px', pointerEvents: 'none' }}
                      src={image}
                      srcWidths={[200]}
                      fallbackWidth={200}
                      alt=""
                    />
                  )}
                </ImageList.ImageHandle>
                <StyledProductImage>
                  <a href={image} target="_blank" rel="noopener noreferrer">
                    View Source
                  </a>
                  <Icon
                    name="trash alternate outline"
                    link
                    onClick={() => {
                      removeImage(image)
                    }}
                  />
                </StyledProductImage>
              </div>
            </ImageList.Image>
          ))}
        </StyledProductImagesHolder>
      </ImageList.Images>
    </FileDrop>
  )
}

export const Localizations = ({ productId, mode }: Props) => {
  const _value = useLocaleSpecificContext()
  const setValue = useLocaleSpecificUpdateContext()
  const value = _value.toUpperCase()
  const [lastSave, setLastSave] = useState<Date | null>(null)
  const dispatch = useDispatch()
  const localizations: UILocalizations = useSelector(
    (state: AppState) => state.products.localizations
  )

  const { initialProductValues } = useSelector((state: AppState) => state.ui)

  const productUuid = useSelector((state: AppState) => state.products.productUuid)

  const productName = initialProductValues.name

  const productDescription = initialProductValues.description

  const productLineage = initialProductValues.lineage

  const productBrandSubtype = initialProductValues.brand_subtype

  const country = useMemo(() => (value ? value.split('-')[0].toUpperCase() : null), [value])
  const subdivision = useMemo(() => (value ? value.split('-')[1].toUpperCase() : null), [value])

  const defaultNewLocalization = useMemo(
    () =>
      ({
        id: '',
        catalog_product_id: productUuid,
        country,
        subdivision,
        images: [],
        name: productName,
        description: productDescription,
        lineage: productLineage,
        category_label: productBrandSubtype,
      } as ApiLocalization),
    [
      country,
      productUuid,
      subdivision,
      productName,
      productDescription,
      productLineage,
      productBrandSubtype,
    ]
  )

  const currentLocalization: ApiLocalizationWithStatus = useMemo(
    () =>
      value && productId && localizations && localizations[productId]
        ? localizations[productId].find((l: any) => {
            return l.country === country && l.subdivision === subdivision
          }) || defaultNewLocalization
        : defaultNewLocalization,
    [country, defaultNewLocalization, localizations, productId, subdivision, value]
  )

  const nameComponent = () => (
    <Field name="name">
      {(props) => <TextNumberInput props={props} label="Name" placeholder="string" />}
    </Field>
  )

  const descriptionComponent = () => (
    <Field name="description">
      {(props) => (
        <Form.Field>
          <label className="product-form-label">Description</label>
          <TextArea {...props.input} rows="12" placeholder="string" />
        </Form.Field>
      )}
    </Field>
  )

  const lineageComponent = () => (
    <>
      <Form.Field inline>
        <label className="product-form-label">Lineage</label>
        <Field name="lineage">
          {(props) => (
            <>
              <select {...props.input} required={initialProductValues.kind === 'flower'}>
                <SelectionOptions
                  options={[{ ...{ id: '', key: '', text: '', value: '' } }, ...LINEAGE_OPTIONS]}
                />
              </select>
            </>
          )}
        </Field>
      </Form.Field>
      {initialProductValues.kind === 'flower' && <p>Must select lineage when type is flower</p>}
    </>
  )

  const brandSubtypeComponent = () => (
    <Field name="category_label">
      {(props) => (
        <TextNumberInput
          props={props}
          label="Brand subtype"
          placeholder="string"
          fullWidth={true}
        />
      )}
    </Field>
  )

  useEffect(() => {
    if (productId) {
      dispatch(getLocalizations({ productId }))
      setLastSave(null)
    }
  }, [dispatch, productId])

  const save = () => {
    if (productId) {
      setLastSave(new Date())

      dispatch(
        saveLocalizations({
          productId,
          productUuid,
          localizations,
        })
      )
    }
  }

  const handleRemoveLocalization = async (el: React.MouseEvent<HTMLButtonElement>) => {
    el.preventDefault()
    const userProceed = window.confirm(
      "Are you sure you want to delete this state specific content? This action can't be undone."
    )
    if (userProceed) {
      try {
        await axios.delete(`/api/catalog_product_localizations/${currentLocalization.id}`)
        if (productId) {
          dispatch(getLocalizations({ productId }))
          toast('Localization deleted', { containerId: 'default' })
        }
      } catch (error) {
        TrackJS.track(error as any)
        toast('Something went wrong.', { containerId: 'error' })
      }
    }
  }

  const updateLocalizations = ({ values, dirty }: { values: any; dirty: boolean }) => {
    if (currentLocalization && productId && localizations && localizations[productId] && dirty) {
      dispatch(
        setLocalizations({
          productUuid,
          productId,
          localizations: [
            ...localizations[productId].filter(
              (l: ApiLocalization) => l.country !== country || l.subdivision !== subdivision
            ),
            {
              ...currentLocalization,
              name: values.name,
              description: values.description,
              category_label: values.category_label,
              lineage: values.lineage,
            },
          ],
        })
      )
    }
  }

  if (productId && mode === 'edit') {
    return (
      <div>
        <FinalForm initialValues={currentLocalization} onSubmit={save}>
          {(props) => (
            <form onSubmit={props.handleSubmit}>
              <FormSpy onChange={updateLocalizations} />
              <LocaleSpecificSelect value={value} setValue={setValue} />
              <Grid stackable>
                <Grid.Row columns={1}>
                  <Grid.Column>
                    {nameComponent()}
                    {descriptionComponent()}
                    {lineageComponent()}
                    {brandSubtypeComponent()}

                    <ImageList initialImages={currentLocalization.images || []}>
                      <ImagesComponent
                        currentLocalization={currentLocalization}
                        productId={productId}
                        country={country}
                        subdivision={subdivision}
                      />
                    </ImageList>
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row columns={2}>
                  <Grid.Column floated="left">
                    <Button primary type="submit">
                      Save
                    </Button>
                  </Grid.Column>
                  <Grid.Column floated="right">
                    {currentLocalization.id && (
                      <Button color="red" onClick={handleRemoveLocalization}>
                        Remove State
                      </Button>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </form>
          )}
        </FinalForm>

        {lastSave &&
          currentLocalization?.success &&
          currentLocalization.updated &&
          currentLocalization.updated >= lastSave && <Message positive>Saved!</Message>}
        {lastSave &&
          currentLocalization?.failure &&
          currentLocalization.updated &&
          currentLocalization.updated >= lastSave && <Message negative>Failure!</Message>}
      </div>
    )
  } else if (mode === 'create') {
    return <div>State-Specific only available in edit mode</div>
  } else {
    return <div>State-Specific cannot be used on uncreated products</div>
  }
}
